import { Component, ChangeDetectionStrategy, computed, inject, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import { AssessmentRecord } from '../../models';
import { getEffectiveAssessmentState } from '../../utils/assessment.utils';

/**
 * Table-cell review-status chip + inline "Verify now / Submit for
 * sign-off" CTA. Delegates the state derivation to the shared
 * `getEffectiveAssessmentState` helper so the card and the list-view
 * cell say the exact same thing about the same record — no more
 * "Approved" on the card while the table shows "Verified".
 *
 * Type-aware vocabulary comes for free from the helper (DPIA vs AI
 * Risk); the CTA label likewise adapts.
 */
@Component({
  selector: 'app-review-status-badge',
  imports: [MatIconModule, MatTooltipModule, StatusIndicatorComponent, TranslocoModule],
  templateUrl: './review-status-badge.html',
  styleUrl: './review-status-badge.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewStatusBadgeComponent {
  private readonly transloco = inject(TranslocoService);

  /** The assessment record to display review status for. */
  assessment = input.required<AssessmentRecord>();

  /** Whether the current user can request a review (non-manager with completed assessment). */
  canRequestReview = input(false);

  /** Emitted when the user clicks "Verify now" / "Submit for sign-off". */
  requestReview = output<AssessmentRecord>();

  /** The one thing this cell should say — chip OR CTA, type-aware. */
  protected readonly state = computed(() =>
    getEffectiveAssessmentState(this.assessment(), this.canRequestReview(), (k, p) =>
      this.transloco.translate(k, p)
    )
  );

  onRequestReview(event: Event): void {
    event.stopPropagation();
    this.requestReview.emit(this.assessment());
  }
}
